/**
 * `/auth/callback` — where Auth0 (via the BFF) lands after sign-in. Pulls the
 * token from the URL hash, stores it in the shared cookie, then navigates to
 * wherever the user came from.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { processAuthCallback, type CallbackResult } from "@/lib/auth";
import { useAuth } from "@/lib/auth/useAuth";

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  // Runs exactly once during render (idempotent — setTokenCookie is safe to repeat).
  const [result] = useState<CallbackResult>(() => processAuthCallback());

  useEffect(() => {
    if (result.success) {
      refresh();
      // Strip the token-bearing hash from history before redirecting.
      navigate(result.redirectPath, { replace: true });
    }
  }, [result, navigate, refresh]);

  if (!result.success) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm font-medium text-danger-text">Sign-in failed</p>
        <p className="max-w-sm text-xs text-fg-faint">
          {result.error ?? "Authentication failed"}
        </p>
        <a href="/home" className="text-xs text-info-text underline">
          Try again
        </a>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center text-center">
      <p className="text-sm text-fg-faint">Signing you in…</p>
    </div>
  );
}
